import { Calendar, MapPin } from 'lucide-react'
import { useLocation } from 'wouter'
import { getImageUrls } from '@/utils/imageUrls'
import defaultImage from '@/assets/default-image.svg'
import { LazyLoadImage } from 'react-lazy-load-image-component'


export interface NewsArticle {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  author?: number
}

interface NewsCardProps {
  article: NewsArticle
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '')
}

function truncate(text: string, maxLength: number): string {
  const stripped = stripHtml(text)
  if (stripped.length <= maxLength) return stripped
  return stripped.slice(0, maxLength) + '...'
}

export default function NewsCard({ article }: NewsCardProps) {
  const [, navigate] = useLocation()

  const handleClick = () => {
    navigate(`/news/${article.id}`)
  }

  const imageUrls = article.filename ? getImageUrls('news', article.filename) : null

  const formattedDate = article.published_date
    ? new Date(article.published_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const preview = article.content ? truncate(article.content, 80) : null

  return (
    <div
      onClick={handleClick}
      className="relative cursor-pointer group bg-[#212124] shadow-[0_4px_26px_0_rgba(88,88,88,0.3)]
border border-[#464646] overflow-hidden rounded-[12px] h-full min-h-[254px]"
    >
      {imageUrls ? (
        <div className="relative w-full h-full">
          <LazyLoadImage
            src={imageUrls.landscapeThumb}
            alt={article.filename_alt || article.heading}
            effect="opacity"
            wrapperClassName="lazy-image-wrapper"
            className="
              w-full h-full object-cover
              transition-transform duration-300
              group-hover:scale-105
            "
          />
        </div>
      ) : (
        <div className="absolute inset-0 bg-[#212124] flex items-center justify-center">
          <img src={defaultImage} alt="Default News" />
        </div>
      )}

      {/* Article Content */}
      <div className="absolute bottom-0 left-0 right-0 text-white flex flex-col justify-end h-full">
        <div className="relative mt-auto">
          <div className="absolute inset-0 bg-gradient-to-t from-black/100 to-transparent backdrop-blur-lg [mask-image:linear-gradient(to_top,black,transparent)]" />
          <div className="relative px-2 pt-12 pb-[8px]">
            {/* Title */}
            <h3 className="font-semibold text-sm font-figtree line-clamp-2 leading-[16px] mb-1">
              {article.heading}
            </h3>

            {/* Description */}
            {preview && (
              <p className="text-white text-[12px] flex-1 line-clamp-4 font-figtree font-normal leading-[14px]">
                {preview}
              </p>
            )}

            {/* Gradient Divider */}
            <div
              className="h-px mt-[7px] mb-1"
              style={{
                background:
                  'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.25) 15%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.25) 85%, rgba(255,255,255,0) 100%)',
              }}
            />

            {/* Meta Info */}
            <div className="flex items-center gap-4 text-white text-[12px] p-1 font-figtree font-medium justify-between leading-[14px]">
              {formattedDate && (
                <div className="flex items-center gap-1">
                  <span>{formattedDate}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span className="font-figtree">Secrets Hideaway</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
